import { cn } from '@/utils/cn'

/**
 * Padrão visual da toolbar de página (referência: Deals → DealsTopbar + DealsFilters).
 *
 * Alturas:
 * - Tabs / busca / select / botão Filtros: h-10 (40px) na linha de filtros
 * - Botão ícone isolado (metas, ajuda): h-9 w-9 (36px)
 * - Toggle Kanban/Lista (dentro do grupo): h-8 w-8 (32px)
 *
 * Tipografia: text-xs (tabs) | text-sm (inputs, Filtros)
 * Ícones: 14px (tabs, toggle) | 15px (busca, filtros) | 16px (ícone 9×9)
 * Gap entre controles: gap-2 (8px)
 * Shell da página: mb-5, gap-3 entre linhas
 */

export const PAGE_TOOLBAR = {
  shell: 'mb-5 flex flex-col gap-3',
  row: 'flex items-center gap-2',
  filterRow: 'flex min-w-0 flex-wrap items-center gap-2',

  tabList:
    'flex min-w-0 flex-1 items-center gap-1 overflow-x-auto rounded-lg border border-white/[0.06] bg-white/[0.03] p-1 scrollbar-thin',
  tab: 'flex shrink-0 items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-all',
  tabActive: 'bg-accent text-white shadow-sm',
  tabInactive: 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary',

  controlsGroup: 'flex shrink-0 items-center gap-2',
  iconButton:
    'flex h-9 w-9 items-center justify-center rounded-lg border transition-all',
  iconButtonActive: 'border-accent/20 bg-accent/10 text-accent',
  iconButtonInactive:
    'border-[var(--glass-border)] bg-[var(--glass-bg)] text-text-muted hover:bg-white/[0.06]',

  toggleGroup:
    'flex items-center gap-0.5 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-0.5',
  toggleButton: 'flex h-8 w-8 items-center justify-center rounded-md transition-all',
  toggleButtonActive: 'bg-accent text-white shadow-sm',
  toggleButtonInactive: 'text-text-muted hover:text-text-primary',

  searchInput:
    'h-10 w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] py-2 pl-10 pr-9 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-all focus:border-accent/40 focus:outline-none focus:ring-1 focus:ring-accent/20',
  select:
    'h-10 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] py-2 pl-3 pr-8 text-sm text-[var(--text-primary)] focus:border-accent/40 focus:outline-none appearance-none cursor-pointer',
  filterButton:
    'flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-all',
  filterButtonActive: 'border-accent/30 bg-accent/10 text-accent',
  filterButtonInactive:
    'border-[var(--glass-border)] bg-[var(--glass-bg)] text-text-muted hover:bg-white/[0.06] hover:text-text-primary',

  filterBadge:
    'flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white',
  clearButton:
    'hidden h-10 shrink-0 items-center justify-center rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 text-sm font-medium text-text-muted transition-all hover:bg-white/[0.06] hover:text-text-primary sm:flex',
} as const

export function tabClass(isActive: boolean) {
  return cn(PAGE_TOOLBAR.tab, isActive ? PAGE_TOOLBAR.tabActive : PAGE_TOOLBAR.tabInactive)
}

export function iconButtonClass(isActive: boolean) {
  return cn(
    PAGE_TOOLBAR.iconButton,
    isActive ? PAGE_TOOLBAR.iconButtonActive : PAGE_TOOLBAR.iconButtonInactive,
  )
}

export function toggleButtonClass(isActive: boolean) {
  return cn(
    PAGE_TOOLBAR.toggleButton,
    isActive ? PAGE_TOOLBAR.toggleButtonActive : PAGE_TOOLBAR.toggleButtonInactive,
  )
}

export function filterButtonClass(isActive: boolean) {
  return cn(
    PAGE_TOOLBAR.filterButton,
    isActive ? PAGE_TOOLBAR.filterButtonActive : PAGE_TOOLBAR.filterButtonInactive,
  )
}
